/**
 * Per-business browser preferences — ONE localStorage key per business holding
 * a small versioned JSON document, instead of one key per flag:
 *
 *   okc_business_prefs:<businessId>  →  { v: 1, bwr: {...}, featureTags: {...} }
 *
 * Every flag here is business-scoped, so the business id lives in the key and
 * switching business simply reads a different document. Values are tiny
 * preferences, so the non-atomic read-modify-write of localStorage across two
 * tabs cannot lose anything meaningful.
 *
 * Backend-driven state (free-slot counts, Premium lookups) is NOT
 * kept here on purpose — a stale browser copy must never override the server.
 */

const KEY_PREFIX = 'okc_business_prefs'
const VERSION = 1

export interface IBusinessPrefs {
  v: number
  // Bulk WhatsApp Reminder ("1-Click Vasooli")
  bwr: {
    // Free sample already used: "false" is terminal and trusted offline
    trialAvailable?: boolean
    // Sidebar Collection tab pulse ring dismissed (first click of the tab)
    tabPulseSeen?: boolean
    // Last chosen sort on the campaign screen (Android CollectionPreferences.sort)
    sort?: string
    // Has ever run a paid campaign — `paid_status` on the sidebar's BWR icon events
    hasPaidCampaign?: boolean
    // Has ever run a campaign, paid or free slot, on any platform (Android's
    // HasTriedBulkReminder) — routes past the intro and feeds `paid_status`.
    // Does NOT clear the sidebar NEW tag: that is web-usage only (featureTags)
    hasTriedCampaign?: boolean
    // Taps on a customer's WhatsApp "Reminder" button; every 3rd tap shows the bulk
    // reminder nudge (Android ResolveReminderNudge: getBulkWaNudgeClickCount)
    reminderClickCount?: number
  }
  // Settings → Data Security Checkup
  dataSecurity: {
    // When "Backup Now" last finished on this browser (epoch ms)
    lastBackupAt?: number
  }
  // "New feature" tags (utils/featureTags)
  featureTags: {
    // Settings tab clicked once — clears its NEW pill and pulse ring
    settingsPulseSeen?: boolean
    // "UPI QR for collection" row clicked once — clears the row's NEW pill
    upiQrRowClicked?: boolean
    // A bulk reminder (sample, free or paid) was sent FROM WEB — clears the
    // sidebar Collection NEW tag (utils/featureTags)
    bulkReminderSentOnWeb?: boolean
  }
}

type Listener = () => void
const listeners = new Set<Listener>()
const notify = () => listeners.forEach((listener) => listener())

export const subscribeBusinessPrefs = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const defaults = (): IBusinessPrefs => ({
  v: VERSION,
  bwr: {},
  dataSecurity: {},
  featureTags: {}
})

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const key = (businessId: string) => `${KEY_PREFIX}:${businessId}`

// One-time migration of the old one-key-per-flag layout; old keys are removed
const LEGACY_KEYS: Array<{ key: string; apply: (prefs: IBusinessPrefs, value: string) => void }> = [
  {
    key: 'bulk_campaign_trial_available',
    apply: (prefs, value) => {
      prefs.bwr.trialAvailable = value !== 'false'
    }
  },
  {
    key: 'feature_tag_collection_pulse_seen',
    apply: (prefs) => {
      prefs.bwr.tabPulseSeen = true
    }
  },
  {
    key: 'feature_tag_settings_pulse_seen',
    apply: (prefs) => {
      prefs.featureTags.settingsPulseSeen = true
    }
  },
  // Retired flags: nothing reads them any more, the old keys are just cleared
  { key: 'feature_tag_upi_qr_row_seen', apply: () => undefined },
  { key: 'feature_tag_upi_qr_setup_done', apply: () => undefined }
]

const migrateLegacy = (store: Storage, businessId: string, prefs: IBusinessPrefs): boolean => {
  let migrated = false
  LEGACY_KEYS.forEach((legacy) => {
    const legacyKey = `${legacy.key}:${businessId}`
    const value = store.getItem(legacyKey)
    if (value !== null) {
      legacy.apply(prefs, value)
      store.removeItem(legacyKey)
      migrated = true
    }
  })
  return migrated
}

export const getBusinessPrefs = (businessId: string): IBusinessPrefs => {
  const store = storage()
  if (!store || !businessId) {
    return defaults()
  }
  let prefs = defaults()
  try {
    const raw = store.getItem(key(businessId))
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        prefs = {
          ...prefs,
          ...parsed,
          bwr: { ...parsed.bwr },
          dataSecurity: { ...parsed.dataSecurity },
          featureTags: { ...parsed.featureTags }
        }
      }
    }
  } catch {
    // Corrupt value: fall back to defaults (and overwrite on the next write)
  }
  if (migrateLegacy(store, businessId, prefs)) {
    try {
      store.setItem(key(businessId), JSON.stringify(prefs))
    } catch {}
  }
  return prefs
}

// Shallow-merges each section; notifies subscribers only when something changed
export const updateBusinessPrefs = (
  businessId: string,
  patch: {
    bwr?: Partial<IBusinessPrefs['bwr']>
    dataSecurity?: Partial<IBusinessPrefs['dataSecurity']>
    featureTags?: Partial<IBusinessPrefs['featureTags']>
  }
): void => {
  const store = storage()
  if (!store || !businessId) {
    return
  }
  const current = getBusinessPrefs(businessId)
  const next: IBusinessPrefs = {
    v: VERSION,
    bwr: { ...current.bwr, ...patch.bwr },
    dataSecurity: { ...current.dataSecurity, ...patch.dataSecurity },
    featureTags: { ...current.featureTags, ...patch.featureTags }
  }
  if (JSON.stringify(next) === JSON.stringify(current)) {
    return
  }
  try {
    store.setItem(key(businessId), JSON.stringify(next))
  } catch {}
  notify()
}
